import { graphql } from "@/gql";

/** Everything the planner shows for a date range: standalone tasks as the
 * old ``tasks`` query returned them, plus the series runs of the range
 * (virtual and stored alike). Cancelled runs and series templates are left
 * out by the server. Every entry is keyed by ``occurrenceKey``. */
export const GET_PLANNER_OCCURRENCES = graphql(`
  query GetPlannerOccurrences($fromDate: Date!, $toDate: Date!) {
    plannerOccurrences(fromDate: $fromDate, toDate: $toDate) {
      occurrenceKey
      taskId
      seriesId
      seriesTitle
      occurrenceDate
      plannedDate
      plannedUntil
      isVirtual
      isMoved
      version
      isFrozen
      isCancelled
      title
      shortDescription
      description
      availableFrom
      availableUntil
      firstDayAvailable
      lastDayAvailable
      continuesAfterEndDate
      color
      plannedDurationHours
      plannedDurationMinutes
      priority
      responsible
      representatives
      responsibleUsers {
        id
        fullName
      }
      representativeUsers {
        id
        fullName
      }
      requiresTeamwork
      activityCostCenterIds
      selectedCostCenterIds
      status
      progressStatus
      hasPendingReview
      plannedSlots {
        userId
        day
        addedResponsible
      }
    }
  }
`);

/** The planner's side data: people, departments, activities. The selected
 * person's cards come from their day plan (``GetDayPlan``). */
export const GET_PLANNER_OVERVIEW = graphql(`
  query GetPlannerOverview($selectedUserId: ID) {
    plannerData(selectedUserId: $selectedUserId) {
      users {
        id
        fullName
      }
      departments {
        id
        name
      }
      activityTasks {
        id
        title
      }
    }
  }
`);

/** Capacity check before a planner drop. Read only, also for virtual runs:
 * pass ``occurrenceKey`` for series runs and ``taskId`` for standalone
 * tasks. */
export const GET_OCCURRENCE_CAPACITY_CHECK = graphql(`
  query GetOccurrenceCapacityCheck(
    $userId: ID!
    $taskId: ID
    $occurrenceKey: ID
  ) {
    assignmentCapacityCheck(
      userId: $userId
      taskId: $taskId
      occurrenceKey: $occurrenceKey
    ) {
      fits
      fitsPeriod
      fitsDay
      hasBudget
      period
      windowStart
      windowEnd
      budgetMinutes
      bufferPercentage
      committedMinutes
      candidateMinutes
      remainingMinutes
      overbookedMinutes
      unplannedTaskTitles
      day
      dailyLimitMinutes
      dailyCommittedMinutes
      dailyCandidateMinutes
      dailyRemainingMinutes
      dailyOverbookedMinutes
      dailyLimitIsLegalCap
    }
  }
`);

/** Urgent open work of the signed in person, including today's virtual
 * series runs. */
export const GET_MY_OPEN_URGENT_OCCURRENCES = graphql(`
  query GetMyOpenUrgentOccurrences {
    myOpenUrgentOccurrences {
      occurrenceKey
      taskId
      seriesId
      occurrenceDate
      title
    }
  }
`);

/** Escalation notice: urgent open work nobody is responsible for, for
 * Admin and Personaler (whole company) and Teamleiter (their area). */
export const GET_UNOWNED_URGENT_OCCURRENCES = graphql(`
  query GetUnownedUrgentOccurrences {
    unownedUrgentOccurrences {
      occurrenceKey
      taskId
      seriesId
      occurrenceDate
      title
    }
  }
`);
